import { apiDownloadFile, apiFetchJson } from "@/services/apiClient";
import type { RecoveryFilters } from "@/analytics/recovery.api";
import { normalizeRecoveryKpiByOffice, normalizeRecoveryKpiByServiceLine } from "@/lib/recoveryTableNormalizers";
import type { ApiResponseNormalizer } from "@/types/table";
import { appendSortParams, DEFAULT_SORT, type SortState } from "@/lib/tableSort";
import { API_URL } from "@/lib/utils";

/** ISO date string "2026-03-01T00:00:00" → YYYYMM integer 202603 */
function toPeriod(isoDate: string | null): number | null {
  if (!isoDate) return null;
  const [y, m] = isoDate.split("T")[0].split("-").map(Number);
  return y * 100 + m;
}

/** Query string for the two flat KPI tables — use RecoverySemanticModel (date range). */
function buildFlatQs(f: RecoveryFilters): string {
  const p = new URLSearchParams();
  f.managers.forEach(v => p.append("managers", v));
  f.client_partners.forEach(v => p.append("client_partners", v));
  f.assignment_names.forEach(v => p.append("assignment_names", v));
  f.service_lines.forEach(v => p.append("service_lines", v));
  f.legacy_location.forEach(v => p.append("legacy_location", v));
  if (f.date_from) p.append("date_from", f.date_from);
  if (f.date_to)   p.append("date_to",   f.date_to);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

/** Query string for the hierarchy table — uses RecoveryDrillThroughFilters (period). */
function buildHierarchyQs(f: RecoveryFilters): string {
  const p = new URLSearchParams();
  f.managers.forEach(v => p.append("managers", v));
  f.client_partners.forEach(v => p.append("client_partners", v));
  f.assignment_names.forEach(v => p.append("assignment_names", v));
  f.service_lines.forEach(v => p.append("service_lines", v));
  f.legacy_location.forEach(v => p.append("legacy_location", v));
  const period = toPeriod(f.date_from);
  if (period) p.append("period", String(period));
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

export function fetchRecoveryFlatTable(
  endpoint: string,
  filters: RecoveryFilters,
  signal?: AbortSignal,
  sort?: SortState,
): Promise<unknown> {
  const base = `${API_URL}${endpoint}${buildFlatQs(filters)}`;
  const sortParams = new URLSearchParams();
  appendSortParams(sortParams, sort ?? DEFAULT_SORT);
  const sortQs = sortParams.toString();
  const url = sortQs ? `${base}${base.includes("?") ? "&" : "?"}${sortQs}` : base;
  return apiFetchJson(url, { method: "GET", signal });
}

export function fetchRecoveryHierarchy(
  filters: RecoveryFilters,
  signal?: AbortSignal,
  pagination?: { limit: number; skip: number },
  sort?: SortState,
): Promise<unknown> {
  const qs = buildHierarchyQs(filters);
  const pageParams = pagination
    ? `${qs ? "&" : "?"}limit=${pagination.limit}&skip=${pagination.skip}`
    : "";
  const base = `${API_URL}/recovery/drill-through/client-hierarchy${qs}${pageParams}`;
  const sortParams = new URLSearchParams();
  appendSortParams(sortParams, sort ?? DEFAULT_SORT);
  const sortQs = sortParams.toString();
  const url = sortQs ? `${base}${base.includes("?") ? "&" : "?"}${sortQs}` : base;
  return apiFetchJson(url, { method: "GET", signal });
}

export type RecoveryExportOption =
  | "kpi_by_office"
  | "kpi_by_service_line"
  | "client_hierarchy";

export function downloadRecoveryDrillThroughExcel(
  filters: RecoveryFilters,
  exportOptions: RecoveryExportOption[],
): Promise<void> {
  const params = new URLSearchParams();
  filters.managers.forEach((value) => params.append("managers", value));
  filters.client_partners.forEach((value) => params.append("client_partners", value));
  filters.assignment_names.forEach((value) => params.append("assignment_names", value));
  filters.service_lines.forEach((value) => params.append("service_lines", value));
  filters.legacy_location.forEach((value) => params.append("legacy_location", value));
  if (filters.date_from) params.append("date_from", filters.date_from);
  if (filters.date_to) params.append("date_to", filters.date_to);
  if (exportOptions.includes("client_hierarchy")) {
    const period = toPeriod(filters.date_from);
    if (period) params.append("period", String(period));
  }
  exportOptions.forEach((option) => params.append("export_options", option));

  return apiDownloadFile(
    `${API_URL}/recovery/drill-through/export?${params.toString()}`,
    exportOptions.length > 1 ? "Recovery Drill Through Exports.zip" : "recovery-drill-through.xlsx",
  );
}

export type RecoveryFlatReportConfig = {
  id: string;
  title: string;
  endpoint: string;
  normalizer: ApiResponseNormalizer;
  sortable?: boolean;
};

export const recoveryFlatReports: RecoveryFlatReportConfig[] = [
  {
    id: "kpi-by-office",
    title: "Recovery by Office",
    endpoint: "/recovery/drill-through/kpi-by-office",
    normalizer: normalizeRecoveryKpiByOffice,
    sortable: true,
  },
  {
    id: "kpi-by-service-line",
    title: "Recovery by Service Line",
    endpoint: "/recovery/drill-through/kpi-by-service-line",
    normalizer: normalizeRecoveryKpiByServiceLine,
    sortable: true,
  },
];
